// filepath: apps/web/src/auth/silentRefresh.ts
//
// FR-UI-CORE-2 silent token refresh interceptor (AC-4).
//
// Patches `window.fetch` to detect 401 responses on the
// runtime-config-derived trigger host and trigger a silent
// token refresh via the OIDC client. On refresh success, the
// original request is retried with the new token. On refresh
// failure, the request is forwarded to the caller (the new
// `apiFetch` client owns the re-login redirect path; this
// interceptor's role is the silent-refresh step only).
//
// Why this file still exists (post-ISS-377):
//   The new `apiFetch` client (apps/web/src/api/client.ts) is
//   the canonical caller; it drives the 401 → silent-refresh →
//   retry chain explicitly, with typed auth strategies and the
//   BA's mutation-replay guard. The window.fetch patch in this
//   file is the SAFETY NET for any non-apiFetch /api/* call
//   that slipped through the static-guard (e.g. a runtime-injected
//   fetch from a third-party library). The patch is intentionally
//   narrow: it ONLY retries on 401, it ONLY does so for hosts the
//   runtime config marks as trigger hosts, and it does NOT
//   redirect on failure (the apiFetch client owns that path).
//
// ISS-377 (uat-ba note_13124) — runtime-config host derivation:
//   The pre-ISS-377 default was the hard-coded
//   `/\.platform\.example$/i` regex. That worked for the legacy
//   dev / staging platform.example deployments but failed on
//   every other host (uat-ascoa-go.askou.org, the RZT UAT hosts,
//   apex operator, custom-cname production). The new default is
//   `getSilentRefreshTriggerHost()` from `@/runtime/apiRuntime`
//   — a strict superset of the legacy regex. A future contributor
//   may still pass an explicit `refreshTriggerHosts` to override
//   the default (the legacy regex is preserved as the
//   LEGACY_PLATFORM_EXAMPLE_RE export in `apiRuntime.ts`).

import type { AuthContextProps } from "react-oidc-context";
import {
  getSilentRefreshTriggerHost,
  doesHostMatchTrigger,
  LEGACY_PLATFORM_EXAMPLE_RE,
} from "@/runtime/apiRuntime";

export interface SilentRefreshOptions {
  /** The auth context from react-oidc-context. */
  auth: AuthContextProps;
  /**
   * The host pattern that triggers the refresh. Defaults to
   * the runtime-config-derived trigger host (see
   * `getSilentRefreshTriggerHost()` in `@/runtime/apiRuntime`).
   * The interceptor does NOT intercept requests to other
   * origins (defense — never leak tokens to unrelated hosts).
   *
   * Pass a literal `RegExp` to override (the `regexMatches`
   * form) — the existing `*.platform.example` test fixtures
   * use this to pin the legacy default.
   */
  refreshTriggerHosts?: RegExp;
  /**
   * Optional callback invoked on refresh failure. The default
   * is a no-op (the apiFetch client owns the re-login redirect
   * path; this safety-net interceptor returns the original 401
   * to the caller). The existing
   * `silentRefresh.test.ts` test cases pass a `vi.fn()` here
   * to assert the failure callback is invoked — that contract
   * is preserved.
   */
  onRefreshFailure?: () => void | Promise<void>;
}

/**
 * Patches `window.fetch` with a 401-triggered silent refresh.
 *
 * Returns an `uninstall` function that restores the original fetch.
 * The caller is responsible for calling uninstall on unmount.
 *
 * ISS-377 (uat-ba note_13124): on refresh failure, this
 * interceptor does NOT auto-redirect to the login flow. The
 * new `apiFetch` client owns that path (with the
 * `mutationReplayGuard` deciding replay-vs-redirect). The
 * interceptor returns the original 401 to the caller, which
 * is the correct behaviour for non-apiFetch paths: a
 * non-mutation call (e.g. a stale 401 on a TanStack Query
 * refetch) should be retried on the next render when the
 * caller has had a chance to re-resolve the auth state.
 */
export function installSilentRefresh(
  options: SilentRefreshOptions,
): () => void {
  const { auth } = options;
  const onFailure = options.onRefreshFailure ?? (() => undefined);
  // The trigger host: caller override, or runtime-config default.
  // The runtime config is the canonical source — see
  // `getSilentRefreshTriggerHost()` for the host derivation.
  const triggerHostOverride = options.refreshTriggerHosts ?? null;
  const runtimeTriggerHost = getSilentRefreshTriggerHost();

  if (typeof window === "undefined") {
    return () => {};
  }

  const originalFetch = window.fetch.bind(window);

  const patchedFetch = async function silentRefreshFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;

    // We only intercept API calls (those targeting the trigger
    // host). The OIDC client's own token refresh traffic is not
    // intercepted — it goes through `originalFetch` (the
    // library-managed fetch path).
    let host: string | null = null;
    try {
      host = new URL(url, window.location.origin).host;
    } catch {
      host = null;
    }

    // Trigger host check: the caller may pass a literal `RegExp`
    // (test ergonomics, legacy override) or rely on the
    // runtime-config default. The runtime config is preferred
    // when both are available; the literal `RegExp` is the
    // explicit-override path.
    let isTriggerHost = false;
    if (triggerHostOverride !== null) {
      isTriggerHost = host !== null && triggerHostOverride.test(host);
    } else {
      // The runtime-config-derived host is the primary trigger.
      // The legacy `*.platform.example` regex is the secondary
      // trigger — it covers the v1 dev / staging platform.example
      // deployments AND the test environments where the runtime
      // config is not populated. Production runtime config
      // (the resolved TenantConfig.realm_url host) is the
      // authoritative host; the legacy regex is the belt-and-braces
      // fallback for deployments where the realm URL host differs
      // from the SPA's current origin (e.g. a Keycloak realm on a
      // subdomain of the SPA's apex — `keycloak.acme.io` vs the
      // SPA at `acme.io`).
      isTriggerHost = doesHostMatchTrigger(host, runtimeTriggerHost);
      if (!isTriggerHost && host !== null) {
        isTriggerHost = LEGACY_PLATFORM_EXAMPLE_RE.test(host);
      }
    }

    // First attempt — pass the original request through.
    const firstResponse = await originalFetch(input, init);

    // If the response is not 401, or the host is not a trigger
    // host, return the response unchanged.
    if (firstResponse.status !== 401 || !isTriggerHost) {
      return firstResponse;
    }

    // 401 on a trigger host — attempt silent refresh.
    try {
      await auth.signinSilent();
    } catch {
      // Refresh failed. Invoke the (optional) failure callback
      // — the existing test fixtures pass a `vi.fn()` to assert
      // the callback fires. The apiFetch client owns the
      // re-login redirect path; this safety-net interceptor
      // returns the original 401 to the caller. Throwing here
      // would change the long-standing interceptor contract
      // (every existing caller relies on the original 401 being
      // returned so the caller can branch on it).
      await onFailure();
      return firstResponse;
    }

    // Refresh succeeded — retry the original request. The OIDC
    // client's `useAuth()` user is updated on the next render;
    // the in-flight retry uses the same `init` as the original
    // call (the apiFetch client builds a fresh `init` on its
    // own replay path; this safety-net patch re-issues the
    // request with the caller's original headers, which is
    // correct for non-apiFetch callers).
    return originalFetch(input, init);
  };

  window.fetch = patchedFetch as typeof window.fetch;

  return () => {
    window.fetch = originalFetch;
  };
}
